import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ButtonGroupModule, TooltipModule } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';

import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { routes } from '@libs/utils';
import { HealthHistoryDialogData } from './health-history-dialog.interface';
import { HealthHistoryDialogService } from './health-history-dialog.service';
import { HealthHistoryPatientDetailsComponent } from './components/health-history-patient-details/health-history-patient-details.component';
import { HealthHistoryListComponent } from './components/health-history-list/health-history-list.component';
import { HealthHistoryNotesComponent } from './components/health-history-notes/health-history-notes.component';
import { HealthHistoryNotesService } from './components/health-history-notes/health-history-notes.service';
import { PrescriptionsTabService } from './components/prescriptions-tab/prescriptions-tab.service';

@Component({
  selector: 'ffd-health-history-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SvgIconComponent,
    ButtonGroupModule,
    TooltipModule,
    HealthHistoryPatientDetailsComponent,
    HealthHistoryListComponent,
    HealthHistoryNotesComponent,
  ],
  providers: [HealthHistoryNotesService, PrescriptionsTabService],
  templateUrl: './health-history-dialog.component.html',
  styleUrl: './health-history-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HealthHistoryDialogComponent implements OnInit, OnDestroy {
  public dialogData: HealthHistoryDialogData = inject(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef);
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);
  private readonly prescriptionsTabService = inject(PrescriptionsTabService);
  private readonly dialog = inject(Dialog);

  public hasDirtyForm = computed(() => this.healthHistoryDialogService.viewModel().hasDirtyForm);

  @ViewChild('healthHistoryNotesComponent') public healthHistoryNotesComponent!: HealthHistoryNotesComponent;
  @ViewChild('patientDetails') public patientDetails!: HealthHistoryPatientDetailsComponent;

  protected readonly vm = this.healthHistoryDialogService.viewModel;

  public ngOnInit(): void {
    if (this.dialogData?.siteId) {
      this.healthHistoryDialogService.setSiteId(this.dialogData?.siteId);
    }

    if (this.dialogData?.patientId) {
      this.healthHistoryDialogService.setPatientId(this.dialogData?.patientId);
    }

    this.healthHistoryDialogService.init();
    this.prescriptionsTabService.init();
  }

  public closeDialog(): void {
    if (this.hasDirtyForm()) {
      const dialogRef = this.healthHistoryDialogService.openCancelConfirmationDialog();
      dialogRef.closed.subscribe((result) => {
        if (!result?.confirm) {
          return;
        }
        this.resetHealthHistory();
        this.dialogRef.close();
      });
    } else {
      this.resetHealthHistory();
      this.dialogRef.close();
    }
  }

  public ngOnDestroy(): void {
    this.resetHealthHistory();
  }

  protected onDialogClick(): void {
    if (this.healthHistoryNotesComponent.isEdited()) {
      this.healthHistoryNotesComponent.cancelEdit();
      if (this.dialogData?.siteId !== routes.showcaseSiteId) {
        this.healthHistoryDialogService.getPatientNotes();
      }
    }
  }

  protected onPatientNotesClick(event?: MouseEvent): void {
    event?.stopPropagation();
    this.patientDetails.closeAccordion();
  }

  private resetHealthHistory(): void {
    this.healthHistoryDialogService.resetHealthHistory();
  }
}
